import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import {
  UploadFile,
  DownloadForOffline,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Description
} from '@mui/icons-material';
import {
  exportAccountsToCSV,
  exportAccountsToExcel,
  importAccounts,
  downloadImportTemplate
} from '../../../services/accountService';

interface ImportResult {
  total: number;
  successful: number;
  failed: number;
  errors: Array<{
    row: number;
    accountNumber: string;
    error: string;
  }>;
}

const ExportImport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // فلاتر التصدير
  const [exportFilters, setExportFilters] = useState({
    isActive: '',
    type: '',
    accountCategory: ''
  });

  // معالج تصدير CSV
  const handleExportCSV = async () => {
    try {
      setLoading(true);
      setMessage(null);
      
      const blob = await exportAccountsToCSV(exportFilters);
      
      // إنشاء رابط تحميل
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `accounts-${new Date().getTime()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'تم تصدير الحسابات بنجاح' });
    } catch (error: any) {
      console.error('Error exporting to CSV:', error);
      setMessage({ type: 'error', text: error.message || 'حدث خطأ أثناء تصدير الحسابات' });
    } finally {
      setLoading(false);
    }
  };

  // معالج تصدير Excel
  const handleExportExcel = async () => {
    try {
      setLoading(true);
      setMessage(null);
      
      const blob = await exportAccountsToExcel(exportFilters);
      
      // إنشاء رابط تحميل
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `accounts-${new Date().getTime()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'تم تصدير الحسابات بنجاح' });
    } catch (error: any) {
      console.error('Error exporting to Excel:', error);
      setMessage({ type: 'error', text: error.message || 'حدث خطأ أثناء تصدير الحسابات' });
    } finally {
      setLoading(false);
    }
  };

  // معالج اختيار الملف
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // التحقق من نوع الملف
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      const validExtensions = ['.xlsx', '.csv'];
      
      const isValidType = validTypes.includes(file.type) || 
                         validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (!isValidType) {
        setMessage({ type: 'error', text: 'نوع الملف غير مدعوم. يرجى رفع ملف Excel (.xlsx) أو CSV (.csv)' });
        return;
      }
      
      setSelectedFile(file);
      setMessage(null);
    }
  };

  // معالج الاستيراد
  const handleImport = async () => {
    if (!selectedFile) {
      setMessage({ type: 'error', text: 'يرجى اختيار ملف للاستيراد' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);
      
      const result = await importAccounts(selectedFile);
      
      setImportResult(result.results);
      setShowResultDialog(true);
      
      if (result.results.failed === 0) {
        setMessage({ type: 'success', text: result.message });
      } else {
        setMessage({ type: 'info', text: result.message });
      }
      
      // إعادة تعيين الملف المحدد
      setSelectedFile(null);
      
    } catch (error: any) {
      console.error('Error importing accounts:', error);
      setMessage({ type: 'error', text: error.message || 'حدث خطأ أثناء استيراد الحسابات' });
    } finally {
      setLoading(false);
    }
  };

  // معالج تحميل النموذج
  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      setMessage(null);
      
      const blob = await downloadImportTemplate();
      
      // إنشاء رابط تحميل
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'accounts-import-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'تم تحميل النموذج بنجاح' });
    } catch (error: any) {
      console.error('Error downloading template:', error);
      setMessage({ type: 'error', text: error.message || 'حدث خطأ أثناء تحميل النموذج' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        تصدير واستيراد البيانات المالية
      </Typography>

      {/* رسائل التنبيه */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* شريط التحميل */}
      {loading && <LinearProgress sx={{ mb: 3 }} />}

      {/* قسم التصدير */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          تصدير البيانات
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          قم بتصدير البيانات المالية بتنسيق CSV أو Excel للاستخدام في برامج أخرى
        </Typography>

        {/* فلاتر التصدير */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>حالة الحساب</InputLabel>
              <Select
                value={exportFilters.isActive}
                label="حالة الحساب"
                onChange={(e) => setExportFilters({ ...exportFilters, isActive: e.target.value })}
              >
                <MenuItem value="">الكل</MenuItem>
                <MenuItem value="true">نشط</MenuItem>
                <MenuItem value="false">غير نشط</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>نوع الحساب</InputLabel>
              <Select
                value={exportFilters.type}
                label="نوع الحساب"
                onChange={(e) => setExportFilters({ ...exportFilters, type: e.target.value })}
              >
                <MenuItem value="">الكل</MenuItem>
                <MenuItem value="أصول">أصول</MenuItem>
                <MenuItem value="خصوم">خصوم</MenuItem>
                <MenuItem value="حقوق ملكية">حقوق ملكية</MenuItem>
                <MenuItem value="إيرادات">إيرادات</MenuItem>
                <MenuItem value="مصروفات">مصروفات</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>التصنيف</InputLabel>
              <Select
                value={exportFilters.accountCategory}
                label="التصنيف"
                onChange={(e) => setExportFilters({ ...exportFilters, accountCategory: e.target.value })}
              >
                <MenuItem value="">الكل</MenuItem>
                <MenuItem value="أصول متداولة">أصول متداولة</MenuItem>
                <MenuItem value="أصول ثابتة">أصول ثابتة</MenuItem>
                <MenuItem value="خصوم متداولة">خصوم متداولة</MenuItem>
                <MenuItem value="خصوم طويلة الأجل">خصوم طويلة الأجل</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <DownloadForOffline />}
            onClick={handleExportCSV}
            disabled={loading}
          >
            تصدير البيانات كملف CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} /> : <DownloadForOffline />}
            onClick={handleExportExcel}
            disabled={loading}
          >
            تصدير البيانات كملف Excel
          </Button>
        </Box>
      </Paper>

      {/* قسم الاستيراد */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          استيراد البيانات
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          قم باستيراد البيانات المالية من ملفات CSV أو Excel
        </Typography>

        {/* تحميل النموذج */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
            💡 نصيحة: قم بتحميل النموذج أولاً
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            للحصول على أفضل النتائج، قم بتحميل نموذج الاستيراد وملء البيانات فيه
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Description />}
            onClick={handleDownloadTemplate}
            disabled={loading}
          >
            تحميل نموذج الاستيراد
          </Button>
        </Box>

        {/* اختيار الملف */}
        <Box sx={{ mb: 2 }}>
          <input
            accept=".xlsx,.csv"
            style={{ display: 'none' }}
            id="import-file-input"
            type="file"
            onChange={handleFileSelect}
          />
          <label htmlFor="import-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadFile />}
              disabled={loading}
            >
              اختيار ملف
            </Button>
          </label>
          {selectedFile && (
            <Chip
              label={selectedFile.name}
              onDelete={() => setSelectedFile(null)}
              sx={{ ml: 2 }}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        {/* زر الاستيراد */}
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <UploadFile />}
          onClick={handleImport}
          disabled={loading || !selectedFile}
          color="primary"
        >
          استيراد البيانات
        </Button>
      </Paper>

      {/* نافذة نتائج الاستيراد */}
      <Dialog
        open={showResultDialog}
        onClose={() => setShowResultDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          نتائج الاستيراد
        </DialogTitle>
        <DialogContent>
          {importResult && (
            <>
              {/* ملخص النتائج */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<CheckCircle />}
                  label={`نجح: ${importResult.successful}`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<ErrorIcon />}
                  label={`فشل: ${importResult.failed}`}
                  color="error"
                  variant="outlined"
                />
                <Chip
                  label={`الإجمالي: ${importResult.total}`}
                  variant="outlined"
                />
              </Box>

              {/* جدول الأخطاء */}
              {importResult.errors.length > 0 && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                    <Warning sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />
                    الأخطاء والتحذيرات:
                  </Typography>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>الصف</TableCell>
                          <TableCell>رقم الحساب</TableCell>
                          <TableCell>الخطأ</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importResult.errors.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell>{error.row}</TableCell>
                            <TableCell>{error.accountNumber}</TableCell>
                            <TableCell>{error.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResultDialog(false)}>
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExportImport;
